import React from 'react'

import { Stack, Typography } from '@mui/material'
import type { SxProps } from '@mui/system'

import { Card } from '@repo/ui'

interface SettingsSectionProps {
  /**
   * Section name, rendered as an 18px heading INSIDE the panel.
   *
   * OPTIONAL, for one case only: the panel's own content already carries its heading.
   * Notifications is a table whose header row names the columns (Category · Email · SMS),
   * and "Category" belongs in that row, inline with the other two — a section title above
   * it would be a second heading for the same thing.
   *
   * Do NOT omit it merely because the title would repeat the page title. That means the
   * section is mis-named, not that it should be headless: the PAGE names the domain and the
   * SECTION names its content (Appearance → Theme / Text size, Controls → Keyboard
   * shortcuts). Rename it.
   */
  title?: string
  /** Optional one-line explanation, directly under the title. */
  description?: string
  /**
   * Optional control belonging to the section as a whole rather than to any one row —
   * e.g. the Logos light/dark preview toggle. Sits on the title's line.
   */
  action?: React.ReactNode
  children?: React.ReactNode
  /** Vertical gap between the body's children. */
  spacing?: number
  sx?: SxProps
}

/**
 * One section of a settings page: a panel with its title inside it.
 *
 * ## The model, and what it replaced
 *
 * Every section is a panel. There is no contained/uncontained distinction, and no
 * `contained` prop — that is the whole point, so do not add one back.
 *
 * The previous model made the container semantic: a card meant "these controls form a
 * set", so a section holding one toggle got no card while its neighbour did. Compared
 * side by side (Internal → Labs → Section style) that read as arbitrary rather than as
 * meaningful — with one section boxed and the next not, a reader asks why, and the answer
 * ("this one has three controls and that one has two") is not something the page can
 * communicate or the reader can use. The mixed state was the defect, not the head
 * treatment, which is why moving the title around did not fix it in either direction.
 *
 * ## What this costs, stated rather than glossed
 *
 * Once every section has a panel, the panel no longer means "these belong together" — it
 * is a surface holding content, not a claim about it. That semantic reading is retired
 * deliberately. The alternative that keeps it (no panels at all, sections separated by
 * space) was built and compared as variant E; it was rejected because a form's Save/Cancel
 * row wants an anchor and a long page of unpanelled sections reads flat.
 *
 * ## The border stays — borderless was a dark-mode-only luxury
 *
 * An earlier revision of this dropped the border and leaned on the page→paper step alone.
 * That measured 1.277:1 in DARK mode, which reads. In LIGHT mode the same step is
 * #ffffff page against a #fafafa panel — 1.044:1, and inverted: the panel is DARKER than
 * the page, with white already at the ceiling so it cannot go lighter. The panels were
 * effectively invisible.
 *
 * The general point: a LINE needs about 1.3:1 to read as an edge, a FILL needs far more.
 * So a border buys legibility at a contrast a surface step cannot reach in light mode —
 * which is why `Card` says "the border does the separation" for both modes, and why
 * overriding it here was wrong rather than bold.
 *
 * What made settings pages read as a grid of boxes was never the border alone: it was
 * every section boxed AND a flat type hierarchy AND borders at full strength. The other
 * two are fixed (uniform panels, an 18px head inside each), so the border can stay subtle
 * and do its job.
 *
 * Do NOT try to reclaim contrast by lightening the panel instead: at #1a1a1f the dark-mode
 * input fields (#18181b) drop to 1.058:1 against it and effectively disappear. Page, panel
 * and field are one ramp, and moving the middle step breaks the one below it.
 */
export const SettingsSection = ({
  title,
  description,
  action,
  children,
  spacing = 4,
  sx,
}: SettingsSectionProps) => (
  <Stack sx={sx}>
    {/* `margin: 0 0 12px` is baked into Card; the parent Stack owns section spacing, so
        cancel it rather than have two things set the same gap. */}
    <Card sx={{ m: 0, padding: 5 }}>
      {/* 24px from the head to the first field. With no border, the head and the fields
          share one uninterrupted surface, so this gap is the only thing separating a
          section's name from its contents.
          Skipped entirely when there is no title AND no action — an empty head would leave
          24px of dead space above content that is already self-describing. */}
      {(title || action || description) && (
        <Stack spacing={2} sx={{ mb: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title && (
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 18 }}>
                {title}
              </Typography>
            )}
            {action}
          </Stack>
          {description && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {description}
            </Typography>
          )}
        </Stack>
      )}
      <Stack spacing={spacing}>{children}</Stack>
    </Card>
  </Stack>
)
